package com.upcominds.expedite.data.auth

import io.ktor.client.HttpClient
import io.ktor.client.request.*
import io.ktor.client.request.forms.*
import io.ktor.http.Parameters
import io.ktor.http.isSuccess
import io.ktor.client.statement.*
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.sync.Mutex
import kotlinx.coroutines.sync.withLock
import kotlinx.serialization.json.Json
import kotlinx.serialization.json.jsonObject
import kotlinx.serialization.json.jsonPrimitive
import kotlinx.serialization.json.contentOrNull
import kotlinx.coroutines.CancellationException
import kotlinx.coroutines.withTimeout

class AuthRepository(
    private val platformAuth: OidcAuthPlatform,
    private val tokenStorage: TokenStorage,
    private val oidcConfig: OidcConfig,
    private val httpClient: HttpClient
) {
    private val tokenJson = Json { ignoreUnknownKeys = true }
    private val refreshMutex = Mutex()
    private val _sessionExpired = MutableStateFlow(false)

    val sessionExpired: StateFlow<Boolean> = _sessionExpired.asStateFlow()

    fun getTokenStorage(): TokenStorage = tokenStorage

    suspend fun login(): TokenSet {
        val tokens = platformAuth.startLogin()
        require(tokens.accessToken.isNotBlank()) { "Login did not return an access token" }
        if ("offline_access" in oidcConfig.scope.split(' ')) {
            require(!tokens.refreshToken.isNullOrBlank()) { "Login did not return a persistent session" }
        }
        return refreshMutex.withLock {
            tokenStorage.save(tokens)
            _sessionExpired.value = false
            tokens
        }
    }

    suspend fun getValidAccessToken(): String {
        val current = tokenStorage.load()
            ?: throw IllegalStateException("Not logged in")

        val now = nowEpochSeconds()
        val exp = current.expiresAtEpochSeconds
        val stillValid = now <= (exp - 30)

        return if (stillValid) {
            current.accessToken
        } else {
            refreshToken(failedAccessToken = current.accessToken).accessToken
        }
    }

    suspend fun logout() = refreshMutex.withLock {
        val tokens = tokenStorage.load()
        try {
            // Offline tokens survive SSO logout: explicitly revoke them first.
            // If disconnected, local logout still succeeds; server revocation
            // cannot be guaranteed until connectivity is available.
            val refreshToRevoke = tokens?.refreshToken?.takeIf { it.isNotBlank() && "offline_access" in oidcConfig.scope.split(' ') }
            val revoked = refreshToRevoke?.let { refresh ->
                try {
                    withTimeout(10_000) {
                        httpClient.post(oidcConfig.issuer.trimEnd('/') + "/protocol/openid-connect/revoke") {
                            setBody(FormDataContent(Parameters.build {
                                append("token", refresh)
                                append("token_type_hint", "refresh_token")
                                append("client_id", oidcConfig.clientId)
                                oidcConfig.clientSecret?.let { append("client_secret", it) }
                            }))
                        }.status.isSuccess()
                    }
                } catch (error: Exception) {
                    // Do not log tokens, request bodies, or response bodies.
                    if (error is CancellationException && error !is kotlinx.coroutines.TimeoutCancellationException) throw error
                    false
                }
            } ?: true
            if (revoked) platformAuth.logout()
        } finally {
            tokenStorage.clear()
            _sessionExpired.value = false
        }
    }

    internal suspend fun refreshToken(
        failedAccessToken: String? = null
    ): TokenSet = refreshMutex.withLock {
        val storedTokens = tokenStorage.load()
            ?: throw sessionExpiredException()

        // A concurrent request may already have refreshed and stored a newer token.
        if (failedAccessToken != null && storedTokens.accessToken != failedAccessToken &&
            storedTokens.expiresAtEpochSeconds > nowEpochSeconds() + 30) {
            return@withLock storedTokens
        }

        val refresh = storedTokens.refreshToken?.takeIf { it.isNotBlank() }
            ?: throw sessionExpiredException()

        val tokenEndpoint = oidcConfig.issuer.trimEnd('/') +
                "/protocol/openid-connect/token"

        val params = Parameters.build {
            append("grant_type", "refresh_token")
            append("refresh_token", refresh)
            append("client_id", oidcConfig.clientId)
            oidcConfig.clientSecret?.let { append("client_secret", it) }
        }

        // Capture raw response for debugging
        val httpResp = httpClient.post(tokenEndpoint) {
            setBody(FormDataContent(params))
            header("Accept", "application/json")
        }

        val bodyText = httpResp.bodyAsText()

        if (!httpResp.status.isSuccess()) {
            val errorCode = runCatching { tokenJson.parseToJsonElement(bodyText).jsonObject["error"]?.jsonPrimitive?.contentOrNull }.getOrNull()
            // Only an explicit invalid grant proves that the saved session is
            // expired/revoked. Network, server and client-config errors do not.
            if (httpResp.status.value == 400 && errorCode == "invalid_grant") {
                throw sessionExpiredException()
            }
            throw IllegalStateException("Token refresh failed (HTTP ${httpResp.status.value})")
        }

        val dto = try {
            tokenJson.decodeFromString(TokenResponseDTO.serializer(), bodyText)
        } catch (t: Exception) {
            throw IllegalStateException("Token refresh returned an invalid response. Please try again.", t)
        }

        val refreshed = try {
            dto.toTokenSet(
                fallbackRefresh = storedTokens.refreshToken,
                fallbackId = storedTokens.idToken
            )
        } catch (t: Exception) {
            throw IllegalStateException("Token refresh returned an invalid response. Please try again.", t)
        }

        tokenStorage.save(refreshed)
        refreshed
    }

    private fun sessionExpiredException(): SessionExpiredException {
        expireSession()
        return SessionExpiredException()
    }

    private fun expireSession() {
        tokenStorage.clear()
        _sessionExpired.value = true
    }

    // Για iOS (authorization code → token set)
    suspend fun exchangeCodeForTokens(
        code: String,
        codeVerifier: String?,  // για PKCE αργότερα
        redirectUri: String
    ): TokenSet {
        val tokenEndpoint = oidcConfig.issuer.trimEnd('/') +
                "/protocol/openid-connect/token"

        val params = Parameters.build {
            append("grant_type", "authorization_code")
            append("code", code)
            append("redirect_uri", redirectUri)
            append("client_id", oidcConfig.clientId)
            oidcConfig.clientSecret?.let { append("client_secret", it) }
            if (codeVerifier != null) {
                append("code_verifier", codeVerifier)
            }
        }

        val httpResp = httpClient.post(tokenEndpoint) {
            setBody(FormDataContent(params))
            header("Accept", "application/json")
        }

        val bodyText = try { httpResp.bodyAsText() } catch (_: Throwable) { "" }

        if (!httpResp.status.isSuccess()) {
            throw IllegalStateException("Token exchange failed (HTTP ${httpResp.status.value})")
        }

        val dto: TokenResponseDTO = try {
            tokenJson.decodeFromString(TokenResponseDTO.serializer(), bodyText)
        } catch (t: Throwable) {
            throw IllegalStateException("Token exchange returned an invalid response", t)
        }

        val tokenSet = dto.toTokenSet(
            fallbackRefresh = dto.refresh_token,
            fallbackId = dto.id_token
        )

        return tokenSet
    }

    // Convenience check used by the splash screen to decide navigation
    suspend fun isLoggedIn(): Boolean {
        val tokenSet = tokenStorage.load()
        return tokenSet?.accessToken?.isNotBlank() == true &&
                (tokenSet.expiresAtEpochSeconds > nowEpochSeconds() || !tokenSet.refreshToken.isNullOrBlank())
    }
}

class SessionExpiredException(
    cause: Throwable? = null
) : IllegalStateException("Your session has expired. Please sign in again.", cause)
